import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { PageHeader } from '@/components/ui/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, CheckCircle, Image as ImageIcon, Clock, Trash2, LayoutGrid, List } from 'lucide-react';

interface Generation {
  id: string;
  productName: string;
  originalImageUrl: string;
  style: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: string;
}

type ViewMode = 'grid' | 'list';

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem('dashboard-view-mode') as ViewMode) || 'grid';
  });

  useEffect(() => {
    loadGenerations();
    const interval = setInterval(loadGenerations, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem('dashboard-view-mode', viewMode);
  }, [viewMode]);

  const loadGenerations = async () => {
    try {
      const data = await api.get<Generation[]>('/api/generations');
      setGenerations(data);
    } catch (error) {
      console.error('Failed to load generations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!confirm(t('generationDetail.confirmDelete', 'Are you sure you want to delete this generation?'))) {
      return;
    }

    setDeletingId(id);
    try {
      await api.delete(`/api/generations/${id}`);
      setGenerations(generations.filter(g => g.id !== id));
    } catch (error) {
      console.error('Failed to delete generation:', error);
      alert(t('generationDetail.deleteFailed', 'Failed to delete generation'));
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadge = (status: Generation['status']) => {
    const variants = {
      pending: { variant: 'secondary' as const, label: t('dashboard.status.pending', 'Pending'), icon: Clock, className: '' },
      processing: { variant: 'default' as const, label: t('dashboard.status.processing', 'Processing'), icon: Loader2, className: '' },
      completed: { variant: 'default' as const, label: t('dashboard.status.completed', 'Completed'), icon: CheckCircle, className: 'bg-green-500 hover:bg-green-600 text-white' },
      failed: { variant: 'destructive' as const, label: t('dashboard.status.failed', 'Failed'), icon: null, className: '' },
    };

    const config = variants[status];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={`gap-1 ${config.className}`}>
        {Icon && <Icon className="h-3 w-3" />}
        {config.label}
      </Badge>
    );
  };

  const getStyleLabel = (style: string) => {
    const labels: Record<string, string> = {
      CONTEXTO_USO: t('newGeneration.styles.CONTEXTO_USO', 'Contexto de Uso'),
      EDITORIAL_LUXO: t('newGeneration.styles.EDITORIAL_LUXO', 'Editorial de Luxo'),
      LIFESTYLE_MINIMAL: t('newGeneration.styles.LIFESTYLE_MINIMAL', 'Lifestyle Minimalista'),
      STUDIO_PROFISSIONAL: t('newGeneration.styles.STUDIO_PROFISSIONAL', 'Estudio Profissional'),
      // Legacy styles -- display-only fallback for history
      FUNDO_BRANCO_MARKETPLACE: 'Fundo Branco (descontinuado)',
      CLOSE_UP_DETALHE: 'Close-up (descontinuado)',
      AMBIENTE_NATURAL: 'Ambiente Natural (descontinuado)',
      FLAT_LAY: 'Flat Lay (descontinuado)',
      MINIMALISTA_SOMBRAS: 'Minimalista (descontinuado)',
    };
    return labels[style] || style;
  };

  return (
    <DashboardLayout>
      <PageHeader 
        title={t('nav.dashboard', 'Dashboard')}
        subtitle={t('dashboard.subtitle', 'Overview of your generations')}
      />

      {/* Stats Cards */}
      <div className="grid gap-4 md:gap-6 grid-cols-3 mb-6 md:mb-8">
        <Card>
          <CardHeader className="p-3 md:pb-3 md:p-6">
            <CardDescription className="text-xs md:text-sm">{t('dashboard.totalGenerations', 'Total Generations')}</CardDescription>
            <CardTitle className="text-xl md:text-3xl">{generations.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="p-3 md:pb-3 md:p-6">
            <CardDescription className="text-xs md:text-sm">{t('dashboard.completed', 'Completed')}</CardDescription>
            <CardTitle className="text-xl md:text-3xl text-green-600 dark:text-green-400">
              {generations.filter(g => g.status === 'completed').length}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="p-3 md:pb-3 md:p-6">
            <CardDescription className="text-xs md:text-sm">{t('dashboard.processing', 'Processing')}</CardDescription>
            <CardTitle className="text-xl md:text-3xl text-blue-600 dark:text-blue-400">
              {generations.filter(g => g.status === 'processing' || g.status === 'pending').length}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Generations List Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('dashboard.recentGenerations', 'Recent Generations')}
        </h3>
        
        {/* View Mode Toggle */}
        {generations.length > 0 && (
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600' 
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              title="Grid view"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list' 
                  ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600' 
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              title="List view"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-4" />
          <p className="text-gray-600">{t('common.loading', 'Loading...')}</p>
        </div>
      ) : generations.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                <ImageIcon className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('dashboard.empty', 'No generations yet')}
            </h3>
            <p className="text-gray-600 mb-6 max-w-sm mx-auto">
              {t('dashboard.emptyDescription', 'Start by creating your first AI-generated image')}
            </p>
            <Button 
              onClick={() => navigate('/new')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('dashboard.createFirst', 'Create first generation')}
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid gap-4 md:gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {generations.map((gen) => (
            <Card
              key={gen.id}
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden relative"
              onClick={() => navigate(`/generation/${gen.id}`)}
            >
              {/* Delete Button */}
              <button
                onClick={(e) => handleDelete(gen.id, e)}
                disabled={deletingId === gen.id}
                className="absolute top-2 left-2 z-10 p-1.5 md:p-2 bg-white/90 dark:bg-gray-800/90 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                title="Excluir geração"
              >
                {deletingId === gen.id ? (
                  <Loader2 className="h-3 w-3 md:h-4 md:w-4 text-red-600 animate-spin" />
                ) : (
                  <Trash2 className="h-3 w-3 md:h-4 md:w-4 text-red-600" />
                )}
              </button>

              {/* Image Container - Square aspect ratio with object-contain */}
              <div className="relative aspect-square bg-gray-50 dark:bg-gray-800 flex items-center justify-center p-2">
                <img
                  src={gen.originalImageUrl}
                  alt={gen.productName}
                  className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }}
                />
                <div className="hidden absolute inset-0 flex items-center justify-center">
                  <ImageIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" />
                </div>
                <div className="absolute top-2 right-2">
                  {getStatusBadge(gen.status)}
                </div>
              </div>
              
              {/* Info Footer */}
              <CardHeader className="p-2 md:p-3">
                <CardTitle className="text-sm md:text-base line-clamp-1">{gen.productName}</CardTitle>
                <CardDescription className="flex flex-wrap items-center gap-1 md:gap-2 text-[10px] md:text-xs">
                  <Badge variant="outline" className="font-normal text-[10px] md:text-xs px-1 md:px-2">
                    {getStyleLabel(gen.style)}
                  </Badge>
                  <span className="text-gray-400 hidden md:inline">•</span>
                  <span className="hidden md:inline">
                    {new Date(gen.createdAt).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'short',
                    })}
                  </span>
                </CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="space-y-3">
          {generations.map((gen) => (
            <Card
              key={gen.id}
              className="group cursor-pointer hover:shadow-lg transition-all duration-200 overflow-hidden"
              onClick={() => navigate(`/generation/${gen.id}`)}
            >
              <div className="flex items-center gap-4 p-3 md:p-4">
                {/* Thumbnail */}
                <div className="relative w-20 h-20 md:w-24 md:h-24 shrink-0 bg-gray-50 dark:bg-gray-800 rounded-lg flex items-center justify-center overflow-hidden">
                  <img
                    src={gen.originalImageUrl}
                    alt={gen.productName}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }}
                  />
                  <div className="hidden absolute inset-0 flex items-center justify-center">
                    <ImageIcon className="h-7 w-7 text-gray-300 dark:text-gray-600" />
                  </div>
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h4 className="font-semibold text-gray-900 dark:text-white truncate text-sm md:text-base">
                        {gen.productName}
                      </h4>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge variant="outline" className="font-normal text-xs">
                          {getStyleLabel(gen.style)}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {new Date(gen.createdAt).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {getStatusBadge(gen.status)}
                      <button
                        onClick={(e) => handleDelete(gen.id, e)}
                        disabled={deletingId === gen.id}
                        className="p-2 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                        title="Excluir geração"
                      >
                        {deletingId === gen.id ? (
                          <Loader2 className="h-4 w-4 text-red-600 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-red-600" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
